import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from '../api/axios';
import { DataGrid } from '@mui/x-data-grid';
import {
	Box,
	Typography,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	IconButton,
	CircularProgress,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	FormHelperText,
} from '@mui/material';
import Layout from '../components/Layout';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';
import { red } from '@mui/material/colors';
import { LoadingButton } from '@mui/lab';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

export default function Sales() {
	const user_id = localStorage.getItem('user_id');
	const role = localStorage.getItem("role");
	const [open, setOpen] = useState(false);
	const [editingId, setEditingId] = useState(null);
	const [confirmDeleteId, setConfirmDeleteId] = useState(null);
	const [error, setError] = useState('');
	const [submittingUser, setSubmittingUser] = useState(false);
	const [deletingUser, setDeletingUser] = useState(false);

	const [rows, setRows] = useState([]);
	const [summaryRows, setSummaryRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [summaryLoading, setSummaryLoading] = useState(false);
	const [searchText, setSearchText] = useState('');
	const [summarySearchText, setSummarySearchText] = useState('');
	const [filterStartDate, setFilterStartDate] = useState(null);
	const [filterEndDate, setFilterEndDate] = useState(null);

	const [rowCount, setRowCount] = useState(0);
	const [summaryRowCount, setSummaryRowCount] = useState(0);

	const [paginationModel, setPaginationModel] = useState({
		page: 0,
		pageSize: 25,
	});

	const [summaryPaginationModel, setSummaryPaginationModel] = useState({
		page: 0,
		pageSize: 10,
	});

	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(25);

	const [summaryPage, setSummaryPage] = useState(0);
	const [summaryPageSize, setSummaryPageSize] = useState(10);

	const [appliedSearch, setAppliedSearch] = useState('');
	const [appliedSummarySearch, setAppliedSummarySearch] = useState('');
	const [appliedDateRange, setAppliedDateRange] = useState({
		start: null,
		end: null,
	});

	const [sortModel, setSortModel] = useState([
		{ field: 'created_at', sort: 'desc' },
	]);

	const [summarySortModel, setSummarySortModel] = useState([
		{ field: 'created_at', sort: 'desc' },
	]);

	const fetchProductSalesRecord = useCallback(async () => {
		setLoading(true);
		try {
			const params = {
				sort_field: sortModel[0]?.field || 'created_at',
				sort_order: sortModel[0]?.sort || 'desc',
				page: paginationModel.page,
				pageSize: paginationModel.pageSize,
				filterStartDate: appliedDateRange.start
					? appliedDateRange.start.format('YYYY-MM-DD')
					: null,
				filterEndDate: appliedDateRange.end
					? appliedDateRange.end.format('YYYY-MM-DD')
					: null,
			};

			if (appliedSearch) params.search = appliedSearch;

			const res = await axios.get('/productSales', { params });

			const { items = [], total = 0 } = res.data;

			const formatted = items.map((t) => ({
				...t,
				id: t.id,
				sold_at: t.sold_at ? t.sold_at.slice(0, 10) : '',
			}));

			setRows(formatted);
			setRowCount(total);
		} catch (err) {
			console.error("Fetch error:", err);
		} finally {
			setLoading(false);
		}
	}, [
		paginationModel,
		sortModel,
		appliedSearch,
		appliedDateRange,
	]);

	const fetchSummary = useCallback(async () => {
		setSummaryLoading(true);
		try {
			const params = {
				sort_field: summarySortModel[0]?.field || 'created_at',
				sort_order: summarySortModel[0]?.sort || 'desc',
				page: summaryPaginationModel.page,
				pageSize: summaryPaginationModel.pageSize,
			};

			if (appliedSummarySearch) {
				params.search = appliedSummarySearch;
			}

			const res = await axios.get('/productSalesSummary', { params });

			const { items = [], total = 0 } = res.data;

			const formatted = items.map((t) => ({
				...t,
				id: t.to_site_id,
				last_sold_date: t.last_sold_date
					? t.last_sold_date.slice(0, 10)
					: '',
			}));

			setSummaryRows(formatted);
			setSummaryRowCount(total);
		} catch (err) {
			console.error('Fetch summary error:', err);
		} finally {
			setSummaryLoading(false);
		}
	}, [
		summaryPaginationModel,
		summarySortModel,
		appliedSummarySearch,
	]);


	const handleSearch = () => {
		setAppliedSearch(searchText);
		setAppliedDateRange({
			start: filterStartDate,
			end: filterEndDate,
		});
		setPaginationModel((prev) => ({ ...prev, page: 0 }));
	};

	const handleSummarySearch = () => {
		setAppliedSummarySearch(summarySearchText);
		setSummaryPaginationModel((prev) => ({ ...prev, page: 0 }));
	};

	useEffect(() => {
		fetchProductSalesRecord();
	}, [fetchProductSalesRecord]);

	useEffect(() => {
		fetchSummary();
	}, [fetchSummary]);

	const resetFilter = () => {
		setSearchText('');
		setFilterStartDate(null);
		setFilterEndDate(null);

		setAppliedSearch('');
		setAppliedDateRange({ start: null, end: null });

		setPaginationModel((prev) => ({ ...prev, page: 0 }));
	};

	const resetSummaryFilter = () => {
		setSummarySearchText('');
		setAppliedSummarySearch('');
		setSummaryPaginationModel((prev) => ({ ...prev, page: 0 }));
	};

	const summaryColumns = [
		{ field: "job_code", headerName: "Job Code", width: 100 },
		{ field: "name", headerName: "地盤名稱", width: 160 },
		{
			field: "product_names",
			headerName: "貨物名稱",
			width: 200,
			sortable: false,
			renderCell: (params) => {
				const items = params.row.sales_summary || [];
				return (
					<div style={{ whiteSpace: "normal" }}>
						{items.map(
							(i, idx) =>
								i.amount !== 0 &&
								i.product_name && <div key={idx}>{i.product_name}</div>
						)}
					</div>
				);
			},
		},
		{
			field: "product_amounts",
			headerName: "貨物數量",
			width: 120,
			sortable: false,
			renderCell: (params) => {
				const items = params.row.sales_summary || [];
				return (
					<div style={{ whiteSpace: "normal" }}>
						{items.map(
							(i, idx) =>
								i.amount !== 0 &&
								i.product_name && <div key={idx}>{i.amount}</div>
						)}
					</div>
				);
			},
		},
		{
			field: "price",
			headerName: "售價",
			width: 120,
			renderCell: (params) => {
				const items = params.row.sales_summary || [];
				return (
					<div style={{ whiteSpace: "normal" }}>
						{items.map(
							(i, idx) =>
								i.price !== 0 &&
								i.product_name && <div key={idx}>{i.price}</div>
						)}
					</div>
				);
			},
		},
		{ field: "total_price", headerName: "總售價", width: 100 },
		{ field: 'last_sold_date', headerName: '最後售賣日期', width: 180, minWidth: 150, maxWidth: 220 },
	];

	const columns = [
		{ field: "job_code", headerName: "Job Code", width: 100 },
		{ field: "from_site_name", headerName: "由地盤", width: 160 },
		{ field: "to_site_name", headerName: "到地盤", width: 160 },
		{ field: "product_name", headerName: "貨物名稱", width: 160 },
		{ field: "amount", headerName: "貨物數量", width: 160 },
		{ field: "price", headerName: "售價", width: 160 },
		{ field: "remark", headerName: "備註", width: 160 },
		{ field: "sold_at", headerName: "售賣日期", width: 160 },
	];

	return (
		<Layout>
			<Typography variant="h5" mb={2} sx={{ paddingLeft: '8px' }}>賣貨總覽</Typography>
			<Box className="filter-container" sx={{ display: 'flex', gap: 1, paddingLeft: '8px', mb: 2, alignItems: 'center' }}>
				<TextField
					sx={{ width: 300 }}
					variant="outlined"
					size="small"
					placeholder="搜尋Job code、地盤名稱或貨物名稱"
					value={summarySearchText}
					onChange={(e) => setSummarySearchText(e.target.value)}
				/>
				<Button
					variant="contained"
					// onClick={fetchSummary}
					onClick={handleSummarySearch}
				>搜尋</Button>
				<Button
					variant="contained"
					onClick={resetSummaryFilter}
				>重設</Button>
			</Box>

			{summaryLoading ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
					<CircularProgress />
				</Box>
			) : (
				<Box sx={{ display: 'flex', flexDirection: 'column', p: 1 }}>
					<DataGrid
						disableColumnMenu={true}
						rows={summaryRows}
						columns={summaryColumns}
						rowCount={summaryRowCount}
						pageSize={10}
						getRowHeight={() => 'auto'}
						disableRowSelectionOnClick
					/>
				</Box>
			)}





			<Typography variant="h5" mb={2} sx={{ paddingLeft: '8px', marginTop: "32px" }}>賣貨詳細紀錄</Typography>
			<Box className="filter-container" sx={{ display: 'flex', gap: 1, paddingLeft: '8px', mb: 2, alignItems: 'center' }}>
				<TextField
					sx={{ width: 300 }}
					variant="outlined"
					size="small"
					placeholder="搜尋Job code、地盤名稱或貨物名稱"
					value={searchText}
					onChange={(e) => setSearchText(e.target.value)}
				/>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
						<DatePicker
							label="開始日期"
							format="YYYY/MM/DD"
							maxDate={dayjs()}
							value={filterStartDate}
							onChange={(newValue) => setFilterStartDate(newValue)}
							slotProps={{ textField: { size: 'small' } }}
						/>

						<DatePicker
							label="結束日期"
							format="YYYY/MM/DD"
							maxDate={dayjs()}
							value={filterEndDate}
							onChange={(newValue) => setFilterEndDate(newValue)}
							slotProps={{ textField: { size: 'small' } }}
						/>
					</Box>
				</LocalizationProvider>
				<Button
					variant="contained"
					// onClick={fetchProductSalesRecord}
					onClick={handleSearch}
				>搜尋</Button>
				<Button
					variant="contained"
					onClick={resetFilter}
				>重設</Button>
			</Box>

			{loading ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
					<CircularProgress />
				</Box>
			) : (
				<Box sx={{ display: 'flex', flexDirection: 'column', p: 1 }}>
					<DataGrid
						rows={rows}
						columns={columns}
						getRowHeight={() => 'auto'}
						disableRowSelectionOnClick
						paginationMode="server"
						sortingMode="server"
						rowCount={rowCount}
						paginationModel={paginationModel}
						onPaginationModelChange={setPaginationModel}
						sortModel={sortModel}
						disableColumnMenu={true}
						onSortModelChange={(model) => {
							setSortModel(model);
							setPaginationModel((prev) => ({ ...prev, page: 0 }));
						}}
					/>
				</Box>
			)}




		</Layout>
	)
}